import React, { useState, useEffect } from 'react';
import { X, Info, User, Share2, Users, Loader2 } from 'lucide-react';
import { vaultSphereAPI } from '../services/api';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  commenter: 'Commenter',
  editor: 'Editor',
};

interface VaultSphereDetailsModalProps {
  item: { id: string; name: string; type: 'file' | 'folder' };
  onClose: () => void;
}

export const VaultSphereDetailsModal: React.FC<VaultSphereDetailsModalProps> = ({ item, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    isOwner: boolean;
    owner?: { userId: string; name?: string; email?: string };
    sharedBy?: { userId: string; name?: string; email?: string };
    sharedViaTeam?: { teamId: string; teamName?: string };
    role?: string;
    sharedWith?: unknown[];
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const api = item.type === 'file' ? vaultSphereAPI.getFileSharedDetails : vaultSphereAPI.getFolderSharedDetails;
    api(item.id)
      .then((res) => {
        setData(res.data?.data ?? null);
      })
      .catch((err: unknown) => {
        const ax = err as { response?: { data?: { error?: string }; status?: number } };
        const msg = typeof ax?.response?.data?.error === 'string'
          ? ax.response!.data!.error
          : ax?.response?.status === 403
            ? 'Access denied.'
            : 'Unable to load the details.';
        setError(msg);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [item.id, item.type]);

  const displayOwner = data?.owner?.name || data?.owner?.email || '—';
  const displaySharedBy = data?.sharedBy?.name || data?.sharedBy?.email || null;
  const displayTeam = data?.sharedViaTeam?.teamName || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Info className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Details &quot;{item.name}&quot;</h2>
              <p className="text-xs text-slate-500">{item.type === 'folder' ? 'Folder' : 'File'} shared with you</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : data ? (
            <>
              {!data.isOwner && (
                <div className="flex flex-col gap-4">
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    <p className="font-medium">Vous ne pouvez pas partager ce fichier.</p>
                    <p className="mt-1 text-amber-700">You can only view and download it.</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500">Owner (source)</p>
                        <p className="font-medium text-slate-900">{displayOwner}</p>
                        {data.owner?.email && <p className="text-xs text-slate-600">{data.owner.email}</p>}
                      </div>
                    </div>

                    {displaySharedBy && (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Share2 className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500">Shared by</p>
                          <p className="font-medium text-slate-900">{displaySharedBy}</p>
                        </div>
                      </div>
                    )}

                    {displayTeam && (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500">Team</p>
                          <p className="font-medium text-slate-900">{displayTeam}</p>
                        </div>
                      </div>
                    )}

                    {data.role && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="font-medium">Your access:</span>
                        <span>{ROLE_LABELS[data.role] ?? data.role}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {data.isOwner && (
                <p className="text-sm text-slate-600">You are the owner.</p>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VaultSphereDetailsModal;
